import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Tooltip, Icon } from 'grab-ui';
import _get from 'lodash/get';
import _some from 'lodash/some';
import offersClickSvg from 'public/icons/bulkupload/offers-click.svg';
import I18n from 'i18n-js';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import NewBookingDuck from 'ducks/new-booking-duck';
import { truncateText } from 'helper/anytime-booking';
import { deliveriesFromPropsSelector } from 'selectors/new-booking-selectors';
import { newBookingOverlayType } from 'constants/new-booking-constants';
import {
  bulkUploadApplicableOfferConfigSelector,
  bulkUploadOfferConfigSelector,
} from 'selectors/new-bulk-upload-selectors';
import usePrevious from 'common/usePrevious';
import OfferWarningModal from 'views/v2/new-booking-page/offer-warning-modal/offer-warning-modal';

import promoIcon from 'public/icons/promo-icon.svg';
import warningIcon from 'public/icons/warning-yellow.svg';

import styles from './offer-selector.less';
import IneligibleOfferModal from './ineligible-offer-modal';
import { PayAndBookContext } from '../common/context';
import scribeNewBulkUploadDuck from '../../../../ducks/scribe-new-bulk-upload-duck';
import ScribeDuck from '../../../../ducks/scribe-duck';

const NAME_MAX_LENGTH = 18;
const OfferSelector = ({
  dispatch,
  offerConfig,
  applicableOfferConfig,
  deliveryIDs,
  deliveries,
}) => {
  const [showIneligibleOfferModal, setShowIneligibleOfferModal] = useState(
    false,
  );
  const discountConfig = _get(applicableOfferConfig, 'discountConfig');
  const prevDiscountConfig = usePrevious(discountConfig);

  const { totalFinalFare } = useContext(PayAndBookContext);

  useEffect(
    () => {
      if (discountConfig && !prevDiscountConfig) {
        setShowIneligibleOfferModal(true);
      }
    },
    [discountConfig, prevDiscountConfig],
  );

  const handleOnCloseIneligibleOfferModal = useCallback(
    () => {
      dispatch(NewBulkUploadDuck.creators.setApplicableOfferConfig(null));
      setShowIneligibleOfferModal(false);
    },
    [dispatch, setShowIneligibleOfferModal],
  );

  const handleOnContinueWithoutOfferWarningModal = useCallback(
    () => {
      dispatch(NewBulkUploadDuck.creators.trySetOfferConfig(null));
      setShowIneligibleOfferModal(false);
    },
    [dispatch, setShowIneligibleOfferModal],
  );

  const handleOnSubmitIneligibleOfferModal = useCallback(
    () => {
      dispatch(NewBulkUploadDuck.creators.applyApplicableOfferConfig());
      setShowIneligibleOfferModal(false);
    },
    [dispatch, setShowIneligibleOfferModal],
  );

  const onClickOfferButton = useCallback(
    () => {
      dispatch(NewBulkUploadDuck.creators.fetchOfferList());
      dispatch(
        NewBookingDuck.creators.openBulkEditOverlay(
          deliveryIDs,
          newBookingOverlayType.offerConfiguration,
        ),
      );
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBukUploadBookOffers(
          offerConfig,
          totalFinalFare,
        ),
      );
    },
    [deliveryIDs, offerConfig, totalFinalFare],
  );

  const fetchingQuote = useMemo(
    () => _some(deliveries, delivery => delivery.fetchingQuote),
    [deliveries],
  );

  const [discountEligibilityError, errorMessage] = useMemo(
    () => {
      const discountEligibilityError = _get(
        offerConfig,
        'discountEligibilityError',
        null,
      );
      let errorMessage;
      if (discountEligibilityError) {
        errorMessage =
          _get(discountEligibilityError, 'localizedMessage') ||
          I18n.t('v2.error.invalidPromo');
      }
      return [discountEligibilityError, errorMessage];
    },
    [offerConfig],
  );

  const offerIconPrefix = useMemo(
    () => {
      if (!offerConfig) {
        return <img className={styles.icon} src={promoIcon} alt="" />;
      }

      if (fetchingQuote && offerConfig) {
        return <Icon type="loading" style={{ fontSize: 24 }} spin />;
      }

      if (!discountEligibilityError) {
        return <img className={styles.icon} src={promoIcon} alt="" />;
      }
      return (
        <Tooltip title={errorMessage} placement={'top'}>
          <img className={styles.icon} src={warningIcon} alt="" />
        </Tooltip>
      );
    },
    [fetchingQuote, offerConfig, discountEligibilityError, errorMessage],
  );

  useEffect(
    () => {
      if (offerConfig) {
        // Bulk not display invalid offer
        dispatch(
          ScribeDuck.creators.scribeExpressReviewOfferListApplyOffer('SUCCESS'),
        );
      }
    },
    [dispatch, offerConfig],
  );

  const warningModal = useMemo(
    () => {
      if (!showIneligibleOfferModal) {
        return null;
      }
      if (Object.keys(discountConfig).length === 0) {
        return (
          <OfferWarningModal
            handleClose={handleOnCloseIneligibleOfferModal}
            handleSubmit={handleOnContinueWithoutOfferWarningModal}
            visible={showIneligibleOfferModal}
          />
        );
      }

      return (
        <IneligibleOfferModal
          key="offer-selector-modal"
          handleClose={handleOnCloseIneligibleOfferModal}
          visible={showIneligibleOfferModal}
          handleSubmit={handleOnSubmitIneligibleOfferModal}
          deliveryCount={
            discountConfig ? Object.keys(discountConfig).length : 0
          }
        />
      );
    },
    [
      showIneligibleOfferModal,
      handleOnCloseIneligibleOfferModal,
      handleOnSubmitIneligibleOfferModal,
      handleOnContinueWithoutOfferWarningModal,
      discountConfig,
    ],
  );

  return [
    warningModal,
    <Button
      key="offer-selector-button"
      className={styles.offerButton}
      onClick={onClickOfferButton}
    >
      {offerIconPrefix}
      <span className={styles.name}>
        {offerConfig
          ? truncateText(_get(offerConfig, 'name'), NAME_MAX_LENGTH)
          : I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_button_apply_offer_offers',
            )}{' '}
      </span>
      <img src={offersClickSvg} alt="" className={styles.rightArrow} />
    </Button>,
  ];
};

OfferSelector.defaultProps = {
  offerConfig: null,
};

OfferSelector.propTypes = {
  dispatch: PropTypes.func.isRequired,
  offerConfig: PropTypes.shape({}),
  deliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  deliveries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default connect((state, props) => ({
  deliveries: deliveriesFromPropsSelector(state, props),
  offerConfig: bulkUploadOfferConfigSelector(state),
  applicableOfferConfig: bulkUploadApplicableOfferConfigSelector(state),
}))(OfferSelector);
